import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { Button } from '@helios/ui/button';
import { useAuth } from '@helios/auth-firebase-web';
import { usePowerSyncDb } from '../../adapters/powersync';
import { type ShellOutletContext } from '../../shell/shared';
import { InventoryPageShell } from './_shared';
import { useReceiveDeliveryOrderForm, ReceiveDeliveryOrderFormBody } from './_receive-do-form';

export function ReceiveDeliveryOrderPage() {
  const { purchaseOrderId = '' } = useParams<{ purchaseOrderId: string }>();
  const { collapsed = false, onToggleSidebar } = useOutletContext<ShellOutletContext>() ?? {
    collapsed: false,
  };
  const navigate = useNavigate();
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const userId = user?.uid ?? '';
  const userName = user?.displayName ?? user?.email ?? '';
  const { db } = usePowerSyncDb();

  const form = useReceiveDeliveryOrderForm({
    purchaseOrderId,
    orgId,
    userId,
    userName,
    db,
    onSaved: (kind) => {
      navigate(kind === 'receive' ? '/inventory/delivery-orders' : '/inventory/purchase-orders');
    },
  });

  const handleCancel = () => navigate('/inventory/purchase-orders');

  if (!form.po) {
    return (
      <InventoryPageShell
        title="Receive Delivery Order"
        collapsed={collapsed}
        onToggleSidebar={onToggleSidebar}
      >
        <div style={{ padding: 'var(--sp-6)', color: 'var(--text-muted)' }}>
          Loading purchase order…
        </div>
      </InventoryPageShell>
    );
  }

  const disabled = form.saving !== null || form.alreadyReceived !== null;

  return (
    <InventoryPageShell
      title="Receive Delivery Order"
      subtitle={`Against PO ${form.po.po_number ?? form.po.id.slice(0, 8)} · Supplier: ${form.po.supplier_name ?? '—'}`}
      collapsed={collapsed}
      onToggleSidebar={onToggleSidebar}
      primaryAction={
        <>
          <Button variant="outline" size="md" onClick={handleCancel} disabled={disabled}>
            Cancel
          </Button>
          <Button variant="outline" size="md" onClick={form.saveDraft} disabled={disabled}>
            {form.saving === 'draft' ? 'Saving...' : 'Save Draft'}
          </Button>
          <Button variant="gradient" size="md" onClick={form.receive} disabled={disabled}>
            {form.saving === 'receive' ? 'Receiving...' : 'Receive / Finalize'}
          </Button>
        </>
      }
    >
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 'var(--sp-6)',
          background: 'var(--bg-app, #f8fafc)',
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: '0 auto',
          }}
        >
          <ReceiveDeliveryOrderFormBody form={form} />
        </div>
      </div>
    </InventoryPageShell>
  );
}
